import { NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ButtonComponent } from '@shared/components/button/button.component';
import { AngebotAbhlenenDialogBoxComponent } from '@shared/components/dialog-boxes/angebot-abhlenen-dialog-box/angebot-abhlenen-dialog-box.component';
import { Status, User } from '@core/models/user';
import { AdminService } from '@core/services/admin.service';
import { AuthService } from '@core/services/auth.service';
import { environment } from 'src/environments/environment';
import { TranslationDirective } from '@shared/components/translata-modal/translation.directive';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { DialogService } from '@core/services/dialog.service';

type ErrorType = 'noResult' | 'notAccepted' | 'identityError' | 'lastIdentityError' | 'genericError';

@Component({
  selector: 'app-error-page',
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.scss'],
  imports: [SpinnerComponent, 
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    TranslationDirective,
    TranslateModule,
    ButtonComponent,
  ],
  standalone: true,
})
export class ErrorPageComponent implements OnInit {
  public isLoading = false;
  public canBeDisabled = false;
  public errorType: ErrorType;
  public errorMessage: string;
  public hatKredit: boolean;
  public readonly genericErrorMsg = 'Zugang zu Ihrem Bestandskonto ist aktuell nicht möglich';

  public get user(): User {
    return this.auth.user;
  }

  constructor(
    private router: Router,
    public auth: AuthService,
    public dialog: DialogService,
    private adminService: AdminService
  ) {}

  ngOnInit(): void {
    this.setErrorStateFromUrl();
    this.checkKreditOptions();

    this.errorMessage = this.user?.finapi?.payload?.errorMessage || '';

    if (this.errorType === 'genericError') {
      this.auth.updateFinapiStatus(this.user._id).subscribe({
        next: () => console.log('Successfully updated FinAPI status for generic error.'),
        error: (err) => console.error('Failed to update FinAPI status:', err),
      });
    }
  }

  private setErrorStateFromUrl(): void {
    switch (this.router.url) {
      case '/info/no-result-page':
        this.errorType = 'noResult';
        break;
      case '/info/not-accepted':
        this.errorType = 'notAccepted';
        break;
      case '/info/identity-error':
        this.errorType = 'identityError';
        break;
      case '/info/last-identity-error':
        this.errorType = 'lastIdentityError';
        break;
      default:
        this.errorType = 'genericError';
        break;
    }
  }

  private checkKreditOptions(): void {
    const bankWahl = this.user?.questions?.find((q) => q.name === 'bank-wahl')?.answer || {};
    this.hatKredit = !!(bankWahl.kontokorrentkredit || bankWahl.kontoWechselService);
  }

  async acceptOffer(): Promise<void> {
    this.isLoading = true;
    this.canBeDisabled = true;

    try {
      await Promise.all([
        lastValueFrom(this.auth.updateAuswahlProdukt(this.user._id)),
        lastValueFrom(this.auth.updateQuestions(this.user._id)),
      ]);
      await lastValueFrom(this.auth.refreshUser());

      const baseUrl = window.location.origin;
      window.location.href = baseUrl;
    } catch (error) {
      console.error('An error occurred during the acceptOffer process:', error);
      this.isLoading = false;
    } finally {
      this.isLoading = false;
    }
  }

  goBack(): void {
    if (this.errorType === 'genericError') {
      this.openRejectionPopUp();
    } else {
      this.router.navigateByUrl('/');
    }
  }

  async openRejectionPopUp(): Promise<void> {
    const dialogRef = this.dialog.open(AngebotAbhlenenDialogBoxComponent);
    const answer = await lastValueFrom(dialogRef.afterClosed());

    if (answer === true) {
      this.isLoading = true;
      this.canBeDisabled = true;

      try {
        await Promise.all([
          lastValueFrom(this.adminService.updateSubStatus(this.user._id, 'offeringRejected')),
          lastValueFrom(this.auth.changeStatus(this.user._id, Status.inactive)),
          lastValueFrom(this.auth.updateAuswahlProdukt(this.user._id)),
          lastValueFrom(this.auth.updateQuestions(this.user._id)),
        ]);
        await lastValueFrom(this.auth.refreshUser());

        window.location.href = environment.oneDashboardURL;
      } catch (error) {
        console.error('An error occurred during the rejection process:', error);
        this.isLoading = false;
      }
    }
  }
}
